// components/TodaySalesList.js
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../utils/api";
import UichanEffect from "./UichanEffect";
import "../styles/TodaySalesList.css";

const STORAGE_KEY = "todaySalesListState";

const TodaySalesList = ({ storeId }) => {
  const navigate = useNavigate();

  // 売上一覧の元データ
  const [allItems, setAllItems] = useState([]);

  // カテゴリー一覧（select用）
  const [categories, setCategories] = useState([]);

  const salesControllerRef = useRef(null);
  const categoryControllerRef = useRef(null);
  const pollingRef = useRef(null);
  const isFetchingSalesRef = useRef(false);

  useEffect(() => {
    return () => {
      if (salesControllerRef.current) {
        salesControllerRef.current.abort();
        salesControllerRef.current = null;
      }
      if (categoryControllerRef.current) {
        categoryControllerRef.current.abort();
        categoryControllerRef.current = null;
      }
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      isFetchingSalesRef.current = false;
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Date → YYYY-MM-DD（ローカル時刻基準）に変換
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // 保存データを初回だけ読む
  const savedStateRef = useRef(null);
  if (savedStateRef.current === null) {
    try {
      savedStateRef.current = JSON.parse(
        sessionStorage.getItem(STORAGE_KEY) || "null"
      );
    } catch (e) {
      savedStateRef.current = null;
    }
  }

  // 表示対象日
  const [selectedDate, setSelectedDate] = useState(() => {
    return savedStateRef.current?.selectedDate || formatLocalDate(new Date());
  });

  // 選択中カテゴリー
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return savedStateRef.current?.selectedCategory || "";
  });

  // 今日判定
  const todayStr = formatLocalDate(new Date());
  const isToday = selectedDate === todayStr;

  // ===== スクロール用 ref =====
  const listRef = useRef(null);
  const isRestoringRef = useRef(!!savedStateRef.current);
  const prevSelectedDateRef = useRef(selectedDate);

  // 初回だけ復元するための ref
  const restoreScrollRef = useRef(savedStateRef.current?.scrollTop ?? null);
  const restoreDateRef = useRef(savedStateRef.current?.selectedDate ?? null);
  const hasRestoredRef = useRef(false);

  // ===== 演出用 ref =====
  const uichanWrapperRef = useRef(null);
  const lockRef = useRef(null);
  const titleRef = useRef(null);
  const stopRef = useRef(null);
  const schoolRef = useRef(null);
  const uichanRef = useRef(null);
  const ojiRef = useRef(null);
  const carRef = useRef(null);

  // 日付を前後にずらす（未来禁止）
  const moveDate = (days) => {
    const [year, month, day] = selectedDate.split("-").map(Number);
    const base = new Date(year, month - 1, day);
    base.setDate(base.getDate() + days);

    const nextStr = formatLocalDate(base);

    // 今日より未来は進めない
    if (nextStr > todayStr) return;

    setSelectedDate(nextStr);
  };

  // 詳細へ遷移する前に状態を保存
  const saveListState = () => {
    const stateToSave = {
      selectedDate,
      selectedCategory,
      scrollTop: listRef.current?.scrollTop ?? 0,
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  };

  const navigateToDetail = (inventoryId) => {
    saveListState();
    navigate(`/Inventory_detail/${inventoryId}/${storeId}`);
  };

  // ういちゃん歩く演出開始
  const startSequence = () => {
    const stop = stopRef.current;
    const school = schoolRef.current;
    const uichan = uichanRef.current;
    const oji = ojiRef.current;

    if (uichanWrapperRef.current) {
      uichanWrapperRef.current.style.display = "block";
    }

    if (stop && school && uichan && oji) {
      const containerWidth = stop.parentElement.offsetWidth;
      const h3Right = 140;
      const schoolWidth = school.offsetWidth;

      const stopX = (h3Right + (containerWidth - schoolWidth)) / 2;
      stop.style.left = `${stopX - stop.offsetWidth / 2}px`;

      const schoolLeft = containerWidth - schoolWidth;

      uichan.style.left = `${schoolLeft}px`;
      uichan.style.transform = `translateX(0px)`;
      uichan.style.display = "block";

      setTimeout(() => {
        const moveX = stopX - schoolLeft;
        uichan.style.transform = `translateX(${moveX}px)`;

        setTimeout(() => {
          uichan.src = "/media/system_images/UIsmall2.gif";
        }, 10000);

        setTimeout(() => {
          oji.style.left = `140px`;
          oji.style.opacity = 1;
          const ojiMoveX = stopX - 140;
          oji.style.transform = `translateX(${ojiMoveX}px)`;
        }, 15000);
      }, 300);
    }
  };

  // カテゴリー一覧取得
  const fetchCategories = useCallback(async () => {
    if (categoryControllerRef.current) {
      categoryControllerRef.current.abort();
    }

    const controller = new AbortController();
    categoryControllerRef.current = controller;

    try {
      const res = await apiRequest(
        "inventory/categorys/",
        "GET",
        null,
        {},
        true,
        controller.signal
      );
      setCategories(res || []);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("カテゴリ取得失敗:", err);
      setCategories([]);
    } finally {
      if (categoryControllerRef.current === controller) {
        categoryControllerRef.current = null;
      }
    }
  }, []);

  // 指定日の売上取得
  const fetchSales = useCallback(async () => {
    const controller = new AbortController();

    // 古い通信が残っていたら止める
    if (salesControllerRef.current) {
      salesControllerRef.current.abort();
    }
    salesControllerRef.current = controller;

    try {
      const result = await apiRequest(
        `inventory/journal/print/?date=${selectedDate}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      const soldItems = result
        .filter((r) => r.type === "sale")
        .flatMap((r) => r.details.map((d) => ({ ...d, datetime: r.datetime })))
        .filter((d) => d.inventory_id)
        .sort((a, b) => new Date(b.datetime) - new Date(a.datetime));

      const needFallback = soldItems.some(
        (item) =>
          item.sku === undefined ||
          item.stock === undefined ||
          item.category_id === undefined ||
          item.category_name === undefined
      );

      if (!needFallback) {
        setAllItems(soldItems);
        return;
      }

      const inventoryMap = {};

      await Promise.all(
        soldItems.map(async (item) => {
          if (!inventoryMap[item.inventory_id]) {
            try {
              const inv = await apiRequest(
                `inventory/inventory/${item.inventory_id}/`,
                "GET",
                null,
                {},
                true,
                controller.signal
              );

              const store = inv.store_inventory_set?.find((s) => s.store === storeId);

              inventoryMap[item.inventory_id] = {
                stock: store?.stock ?? 0,
                sku: inv.sku ?? "",
                category_id: inv.category?.id ?? "",
                category_name: inv.category?.category_name ?? "未分類",
              };
            } catch (err) {
              if (err.name === "AbortError") return;
              console.error(`inventory ${item.inventory_id} 取得失敗:`, err);
              inventoryMap[item.inventory_id] = {
                stock: "-",
                sku: "",
                category_id: "",
                category_name: "未分類",
              };
            }
          }
        })
      );

      const mergedItems = soldItems.map((item) => ({
        ...item,
        stock:
          item.stock !== undefined
            ? item.stock
            : inventoryMap[item.inventory_id]?.stock ?? "-",
        sku:
          item.sku !== undefined
            ? item.sku
            : inventoryMap[item.inventory_id]?.sku ?? "",
        category_id:
          item.category_id !== undefined
            ? item.category_id
            : inventoryMap[item.inventory_id]?.category_id ?? "",
        category_name:
          item.category_name !== undefined
            ? item.category_name
            : inventoryMap[item.inventory_id]?.category_name ?? "未分類",
      }));

      setAllItems(mergedItems);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("売上取得失敗:", err);
      setAllItems([]);
    } finally {
      if (salesControllerRef.current === controller) {
        salesControllerRef.current = null;
      }
    }
  }, [selectedDate, storeId]);

  const fetchSalesSafe = useCallback(async () => {
    if (isFetchingSalesRef.current) {
      console.log("[TodaySalesList] skip fetchSales (still fetching)");
      return;
    }

    isFetchingSalesRef.current = true;
    try {
      await fetchSales();
    } finally {
      isFetchingSalesRef.current = false;
    }
  }, [fetchSales]);

  // 初回カテゴリー取得
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 日付変更時は取得
  useEffect(() => {
    fetchSalesSafe();
  }, [fetchSalesSafe]);

  // 今日のときだけ30秒更新（前回完了後に次回）
  useEffect(() => {
    stopPolling();

    if (!isToday) return;

    let cancelled = false;

    const loop = async () => {
      if (cancelled) return;

      await fetchSalesSafe();

      if (cancelled) return;
      pollingRef.current = setTimeout(loop, 30000);
    };

    pollingRef.current = setTimeout(loop, 30000);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [isToday, fetchSalesSafe, stopPolling]);

  // データ表示後のスクロール制御
  // - 詳細から戻ってきた直後は保存位置へ復元
  // - 通常の日付変更時は top
  useLayoutEffect(() => {
    if (!listRef.current) return;

    const el = listRef.current;

    const isRestoreTarget =
      !hasRestoredRef.current &&
      restoreScrollRef.current !== null &&
      restoreDateRef.current === selectedDate;

    // 詳細から戻った直後の復元
    if (isRestoreTarget) {
      const savedScrollTop = restoreScrollRef.current;
      const maxScrollTop = el.scrollHeight - el.clientHeight;

      console.log("[TodaySalesList] restore check", {
        savedScrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
        maxScrollTop,
      });

      // まだ高さ不足なら次回に回す
      if (maxScrollTop <= 0) {
        console.log("[TodaySalesList] restore postponed (no scroll area yet)");
        return;
      }

      const targetScrollTop = Math.min(savedScrollTop, maxScrollTop);

      requestAnimationFrame(() => {
        if (!listRef.current) return;

        listRef.current.scrollTop = targetScrollTop;

        console.log("[TodaySalesList] restore applied", {
          targetScrollTop,
          actualScrollTop: listRef.current.scrollTop,
        });

        hasRestoredRef.current = true;
        sessionStorage.removeItem(STORAGE_KEY);
        restoreScrollRef.current = null;
        restoreDateRef.current = null;
        prevSelectedDateRef.current = selectedDate;
      });

      return;
    }

    // selectedDate が本当に変わった時だけ top にする
    if (prevSelectedDateRef.current !== selectedDate) {
      console.log("[TodaySalesList] selectedDate changed -> scrollTop = 0", {
        prev: prevSelectedDateRef.current,
        next: selectedDate,
      });

      el.scrollTop = 0;
      prevSelectedDateRef.current = selectedDate;
      return;
    }

    // 30秒更新など allItems だけ変わった時はスクロール維持
    console.log("[TodaySalesList] keep scroll position", {
      selectedDate,
      scrollTop: el.scrollTop,
    });
  }, [allItems, selectedDate]);

  // 時刻表示整形
  const formatTime = (isoDatetime) => {
    const date = new Date(isoDatetime);
    return date.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // ヘッダー内の🚸位置を初回計算
  useEffect(() => {
    const stop = stopRef.current;
    const school = schoolRef.current;

    if (stop && school) {
      const container = stop.parentElement;
      const containerWidth = container.offsetWidth;
      const schoolWidth = school.offsetWidth;
      const h3Right = 140;

      const stopX = (h3Right + (containerWidth - schoolWidth)) / 2;
      stop.style.left = `${stopX - stop.offsetWidth / 2}px`;
    }
  }, []);

  // パトカー演出
  const handleTriggerCar = () => {
    const car = carRef.current;
    const container = car?.parentElement;
    const oji = ojiRef.current;
    const school = schoolRef.current;
    const list = document.querySelector(".TodaySalesList");

    if (list) {
      list.classList.add("warning");

      setTimeout(() => {
        list.classList.remove("warning");
      }, 7000);
    }

    if (car && container && school && oji) {
      const containerWidth = container.offsetWidth;
      const schoolWidth = school.offsetWidth;

      const carStart = 140;
      const stopX = (140 + (containerWidth - schoolWidth)) / 2;
      const schoolLeft = containerWidth - schoolWidth;

      car.style.left = `${carStart}px`;
      car.style.opacity = 1;

      const toStop = stopX - carStart;
      car.style.transform = `scaleX(-1) translateX(${-toStop}px)`;

      setTimeout(() => {
        oji.style.display = "none";

        if (lockRef.current) {
          lockRef.current.style.display = "block";
        }

        if (uichanRef.current) {
          uichanRef.current.style.display = "none";
        }

        if (uichanWrapperRef.current) {
          uichanWrapperRef.current.style.display = "none";
        }
      }, 7000);

      setTimeout(() => {
        const toSchool = schoolLeft - carStart;
        car.style.transform = `scaleX(-1) translateX(${-toSchool}px)`;
      }, 10000);

      setTimeout(() => {
        car.style.opacity = 0;

        if (lockRef.current) {
          lockRef.current.style.display = "none";
        }
      }, 15000);
    }
  };

  // カテゴリー絞り込み後の表示データ
  const filteredItems = useMemo(() => {
    if (!selectedCategory) return allItems;

    return allItems.filter(
      (item) => String(item.category_id) === String(selectedCategory)
    );
  }, [allItems, selectedCategory]);

  return (
    <div className="TodaySalesListcontainer">
      <div className="TodaySalesList-toolbar">
        <button type="button" onClick={() => moveDate(-1)}>
          ◀ 前日
        </button>

        <span className="TodaySalesList-toolbar-date">{selectedDate}</span>

        <button
          type="button"
          onClick={() => moveDate(1)}
          disabled={isToday}
          className={isToday ? "disabled-btn" : ""}
        >
          翌日 ▶
        </button>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">全カテゴリー</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.category_name}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => {
            setSelectedDate(formatLocalDate(new Date()));
            setSelectedCategory("");
          }}
        >
          今日に戻す
        </button>
      </div>

      <div className="TodaySalesListcontainer-header">
        <h3 ref={titleRef}>🛒 売れた商品</h3>

        <span className="school-stop" ref={stopRef}>
          🚸
        </span>

        <span className="school" ref={schoolRef} onClick={startSequence}>
          🏫
        </span>

        <img
          className="uichan-small"
          ref={uichanRef}
          src="/media/system_images/UIsmall.gif"
          alt="ういちゃん"
          onClick={handleTriggerCar}
        />

        <img
          className="ojisan"
          ref={ojiRef}
          src="/media/system_images/oji.gif"
          alt="おじさん"
          onClick={handleTriggerCar}
        />

        <span className="policecar" ref={carRef}>
          🚓
        </span>
      </div>

      <div className="TodaySalesList" ref={listRef}>
        <div className="lock-overlay" ref={lockRef}></div>

        <ul className="TodaySalesList-list">
          {filteredItems.map((item, idx) => (
            <li key={`${item.inventory_id}-${idx}`}>
              <div
                className={`TodaySalesList-listDivbox ${
                  item.set_components?.length > 0 ? "has-set" : ""
                }`}
                onClick={() => navigateToDetail(item.inventory_id)}
              >
                <img
                  src={`/media/product_images/${item.sku}.jpg?`}
                  alt=""
                  onError={(e) => {
                    e.target.src = "/media/system_images/noimage.jpg";
                  }}
                />

                <div className="info">
                  <div className="name">
                    {item.set_components?.length > 0
                      ? `【セット商品】${item.product_name}`
                      : item.product_name}
                  </div>

                  <div className="meta">
                    {item.category_name} ／ ￥{item.unit_price} ／ 在庫: {item.stock}
                  </div>

                  {item.set_components?.length > 0 ? null : (
                    <div className="time">{formatTime(item.datetime)}</div>
                  )}
                </div>
              </div>

              {item.set_components?.length > 0 && (
                <ul className="set-components">
                  {item.set_components.map((comp, i) => (
                    <li
                      key={i}
                      className="set-component-item"
                      onClick={() => navigateToDetail(comp.id)}
                    >
                      <img
                        src={`/media/product_images/${comp.sku}.jpg?`}
                        alt=""
                        onError={(e) => {
                          e.target.src = "/media/system_images/noimage.jpg";
                        }}
                      />

                      <div className="info">
                        <div className="name">　　　　　　　{comp.item_name}</div>
                        <div className="meta">　　　　　在庫: {comp.stock}</div>

                        {i === item.set_components.length - 1 && (
                          <div className="time">{formatTime(item.datetime)}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        <div className="uichan-fixed-wrapper" ref={uichanWrapperRef}>
          <UichanEffect />
          <img
            className="uichan-overlay"
            src="/media/system_images/UILo.gif"
            alt="ういちゃん"
          />
        </div>
      </div>
    </div>
  );
};

export default TodaySalesList;